import { Card, CardContent, Typography, Box, IconButton } from "@mui/material";
import { MoreVert } from "@mui/icons-material";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
} from "recharts";

const data = [
  { month: "Jan", completed: 80, pending: 38, failed: 12 },
  { month: "Feb", completed: 81, pending: 38, failed: 14 },
  { month: "Mar", completed: 83, pending: 39, failed: 16 },
  { month: "Apr", completed: 82, pending: 40, failed: 18 },
  { month: "May", completed: 85, pending: 41, failed: 21 },
  { month: "Jun", completed: 88, pending: 44, failed: 26 },
  { month: "Jul", completed: 83, pending: 40, failed: 20 },
  { month: "Aug", completed: 88, pending: 43, failed: 28 },
  { month: "Sep", completed: 92, pending: 48, failed: 36 },
  { month: "Oct", completed: 89, pending: 32, failed: 31 },
  { month: "Nov", completed: 90, pending: 32, failed: 30 },
  { month: "Dec", completed: 98, pending: 50, failed: 42 },
];

export default function BookingRevenueTrends({
  data: propData,
  percentages,
}: {
  data?: any[];
  percentages?: {
    successRate: number;
    pendingRate: number;
    failureRate: number;
  };
}) {
  const chartData = propData || data;

  return (
    <Card sx={{ boxShadow: "0 2px 8px rgba(0,0,0,0.1)", mt: 5, p: 0 }}>
      <CardContent sx={{ overflowX: "auto" }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            mb: 3,
            borderBottom: "1px solid #E4E7EC",
            pb: 2,
          }}
        >
          <Box>
            <Typography
              variant="h6"
              sx={{ fontWeight: 600, mb: 0.5, fontSize: { xs: 18, sm: 20 } }}
            >
              Booking & Revenue Trends
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Monitor performance over time and detect issues with
              pending/failed bookings
            </Typography>
          </Box>
          <IconButton size="small">
            <MoreVert />
          </IconButton>
        </Box>

        <Box minWidth={{ xs: "600px", sm: "auto" }} height={300}>
          <ResponsiveContainer width="100%" height="100%">
            <LineChart
              data={chartData}
              margin={{ top: 5, right: 30, left: 0, bottom: 5 }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis
                dataKey="month"
                axisLine={false}
                tickLine={false}
                tick={{ fill: "#666", fontSize: 12 }}
              />
              <YAxis
                axisLine={false}
                tickLine={false}
                tick={{ fill: "#666", fontSize: 12 }}
              />
              <Tooltip
                contentStyle={{
                  backgroundColor: "#fff",
                  border: "1px solid #e0e0e0",
                  borderRadius: "8px",
                  padding: "12px",
                  boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                }}
                labelStyle={{
                  color: "#1a1a1a",
                  fontWeight: 600,
                  marginBottom: "8px",
                  textTransform: "capitalize",
                }}
                itemStyle={{
                  color: "#666",
                  fontSize: "14px",
                  padding: "4px 0",
                }}
              />
              <Line
                type="monotone"
                dataKey="completed"
                stroke="#379E66"
                strokeWidth={2}
                dot={{ fill: "#379E66", r: 4 }}
              />
              <Line
                type="monotone"
                dataKey="pending"
                stroke="#F9A000"
                strokeWidth={2}
                dot={{ fill: "#F9A000", r: 4 }}
              />
              <Line
                type="monotone"
                dataKey="failed"
                stroke="#DA4733"
                strokeWidth={2}
                dot={{ fill: "#DA4733", r: 4 }}
              />
            </LineChart>
          </ResponsiveContainer>
        </Box>

        <Box
          sx={{
            display: "flex",
            justifyContent: "space-around",
            mt: 4,
            borderTop: "1px solid #E4E7EC",
            pt: 2,
          }}
        >
          <Box sx={{ textAlign: "center" }}>
            <Typography
              variant="h5"
              sx={{ color: "#379E66", fontWeight: 600, fontSize: 16 }}
            >
              {percentages?.successRate}
            </Typography>

            <Typography variant="caption" color="#379E66">
              Success Rate
            </Typography>
          </Box>

          <Box sx={{ textAlign: "center" }}>
            <Typography
              variant="h5"
              sx={{ color: "#F9A000", fontWeight: 600, fontSize: 16 }}
            >
              {percentages?.pendingRate}
            </Typography>

            <Typography variant="caption" color="#F9A000">
              Pending Rate
            </Typography>
          </Box>

          <Box sx={{ textAlign: "center" }}>
            <Typography
              variant="h5"
              sx={{ color: "#DA4733", fontWeight: 600, fontSize: 16 }}
            >
              {percentages?.failureRate}
            </Typography>

            <Typography variant="caption" color="#DA4733">
              Failure Rate
            </Typography>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
}
